import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrderStaffPayment } from '../../api/types';
import { formatMoney, parsePaise } from './allocation';
import { formatIstDateTime } from './paymentDates';
import { StatusChip } from './StatusChip';
import { usePaymentProof } from './usePaymentProof';
import styles from './Payments.module.css';
import { Button } from '../../components/ui/Button';

const at = (value: string | null) => (value ? `${formatIstDateTime(value)} IST` : '—');
function PaymentProof({ payment }: { payment: OrderStaffPayment }) {
  const image = /\.(?:png|jpe?g)$/i.test(payment.proofFileName ?? '');
  const proof = usePaymentProof(payment.paymentId, payment.proofAvailable, image, true);
  const [preview, setPreview] = useState(false);
  const { t } = useTranslation();
  if (!payment.proofAvailable) return <>—</>;
  return (
    <>
      {(proof.state === 'none' || proof.state === 'error') && (
        <Button
          variant="secondary" size="sm"
          aria-label={`${proof.state === 'error' ? 'Retry' : 'View'} proof for payment #${payment.paymentId}`}
          onClick={async () => {
            if (await proof.load()) setPreview(true);
          }}
        >
          {proof.state === 'error' ? 'Retry' : 'View proof'}
        </Button>
      )}
      {proof.state === 'loading' && <span>{t('payments.orderPaymentHistory.loadingProof')}</span>}
      {proof.state === 'error' && <span role="alert">{t('payments.orderPaymentHistory.proofUnavailable')}</span>}
      {proof.state === 'ready' && proof.url && (
        <>
          <Button
            variant="secondary" size="sm"
            aria-label={`Preview proof for payment #${payment.paymentId}`}
            onClick={() => setPreview(true)}
          >
            {image ? (
              <img
                className={styles.paymentProofThumb}
                src={proof.url}
                alt={`Proof for payment #${payment.paymentId}`}
              />
            ) : (
              '▤ PDF proof'
            )}
          </Button>
          {preview && (
            <div className={styles.scrim} onClick={() => setPreview(false)}>
              <section
                className={`${styles.dialog} ${styles.compact}`}
                role="dialog"
                aria-modal="true"
                aria-label={t('payments.orderPaymentHistory.previewLabel')}
                onClick={(event) => event.stopPropagation()}
              >
                <div className={styles.body}>
                  <Button variant="secondary" size="sm" onClick={() => setPreview(false)}>
                    {t('payments.orderPaymentHistory.closeProof')}
                  </Button>
                  {image ? (
                    <img className={styles.proofPreview} src={proof.url} alt={t('payments.orderPaymentHistory.proofAlt')} />
                  ) : (
                    <a href={proof.url} target="_blank" rel="noopener noreferrer" title={t('payments.orderPaymentHistory.opensNewTab')}>
                      {t('payments.orderPaymentHistory.openPdfProof')}
                    </a>
                  )}
                </div>
              </section>
            </div>
          )}
        </>
      )}
    </>
  );
}
export function OrderPaymentHistory({
  payments,
  direction,
  onOpen,
}: {
  payments: OrderStaffPayment[];
  direction: 'IN' | 'OUT';
  onOpen: (id: number) => void;
}) {
  const { t } = useTranslation();
  const rows = payments.filter((payment) => payment.direction === direction);
  const kind = direction === 'OUT' ? 'Payout' : 'Receipt';
  return (
    <section className={styles.card}>
      <h3>{direction === 'OUT' ? t('payments.orderPaymentHistory.payoutsHeading') : t('payments.orderPaymentHistory.receiptsHeading')}</h3>
      {!rows.length ? (
        <p>{direction === 'OUT' ? t('payments.orderPaymentHistory.noPayouts') : t('payments.orderPaymentHistory.noReceipts')}</p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{kind}</th>
                <th>{t('payments.orderPaymentHistory.colPurpose')}</th>
                <th>{t('payments.orderPaymentHistory.colAmount')}</th>
                <th>{t('payments.orderPaymentHistory.colStatus')}</th>
                <th>{t('payments.orderPaymentHistory.colMethod')}</th>
                <th>{t('payments.orderPaymentHistory.colPaidAt')}</th>
                <th>{t('payments.orderPaymentHistory.colHistory')}</th>
                <th>{t('payments.orderPaymentHistory.colProof')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((payment) => (
                <tr key={payment.paymentId}>
                  <td>
                    <button className={styles.orderLink} onClick={() => onOpen(payment.paymentId)}>
                      {kind} #{payment.paymentId}
                    </button>
                  </td>
                  <td>
                    {payment.purposes
                      .map((purpose) => purpose.toLowerCase().replace(/_/g, ' '))
                      .join(', ')}
                  </td>
                  <td>
                    <strong>{formatMoney(parsePaise(payment.amount))}</strong>
                    {parsePaise(payment.tdsAmount) > 0n && (
                      <div className={styles.subtle}>
                        {t('payments.orderPaymentHistory.inclTds', { amount: formatMoney(parsePaise(payment.tdsAmount)) })}
                      </div>
                    )}
                  </td>
                  <td>
                    <StatusChip displayStatus={payment.displayStatus} />
                    {payment.displayStatus === 'REJECTED' && (
                      <div className={styles.rejection}>
                        {t('payments.orderPaymentHistory.rejectedBy', {
                          name: payment.rejectedByName ?? '—',
                          time: at(payment.rejectedAt),
                          reason: payment.statusReason ?? '—',
                        })}
                      </div>
                    )}
                  </td>
                  <td>
                    {payment.method ?? '—'}
                    <div>{payment.reference ?? '—'}</div>
                  </td>
                  <td>{at(payment.paidAt)}</td>
                  <td>
                    <div className={styles.paymentAudit}>
                      <span>
                        {t('payments.orderPaymentHistory.requestedBy', { name: payment.requestedByName ?? '—', time: at(payment.requestedAt) })}
                      </span>
                      {payment.approvedAt && (
                        <span>
                          {t('payments.orderPaymentHistory.approvedBy', { name: payment.approvedByName ?? '—', time: at(payment.approvedAt) })}
                        </span>
                      )}
                      {payment.paidRecordedAt && (
                        <span>
                          {t('payments.orderPaymentHistory.paidBy', { name: payment.paidByName ?? '—', time: at(payment.paidRecordedAt) })}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <PaymentProof payment={payment} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
